// Repeatable ATTS containers in the posting-template editor.
//
// A container whose effective max_occurs is > 1 (e.g. Walmart `fabricContent`
// → materialName + materialPercentage) is authored as a table: one row per
// container instance, one column per leaf. The row lives on each LEAF mapping
// as the 0-based `value_index` — no group keys or parent pointers. Only a
// container's direct leaves are slotted; live global sets have no repeat nested
// inside a repeat, so a single int is enough. Root containers are the category
// itself and are never repeatable.

import type { AttsAssignment, AttributeStructure } from '@/endpoints/channel/getAttributeSpec';
import type { MappingsByAssignment, PostingTemplateMapping, TemplateEditorCategory } from '@/types/postingTemplate';

export type RepeatableContainerInfo = {
  assignmentId: number;
  /** Same path the view-model uses for the container (onAddContainerInstance). */
  path: string;
  displayName: string;
  maxOccurs: number;
  leafAssignmentIds: number[];
};

export type RepeatableContainerIndex = {
  byContainerId: Record<string, RepeatableContainerInfo>;
  byPath: Record<string, RepeatableContainerInfo>;
  byLeafId: Record<string, RepeatableContainerInfo>;
};

type ContainerLike = TemplateEditorCategory & { container_path?: string };

/** The container path the posting view-model assigns (container_path → attribute_path → id). */
export const getPostingContainerPath = (container: ContainerLike): string =>
  container.container_path || container.attribute_path || String(container.assignment_id);

/** Row of a leaf mapping; missing/invalid values are row 0 (legacy data). */
export const getValueIndex = (mapping: Pick<PostingTemplateMapping, 'value_index'>): number => {
  const value = Number(mapping.value_index ?? 0);
  return Number.isInteger(value) && value >= 0 ? value : 0;
};

/**
 * Effective container max_occurs: the assignment overrides the attribute,
 * falling back to the structure node (matches the backend spec factory).
 */
export const getContainerMaxOccurs = (container: ContainerLike, assignment: AttsAssignment | undefined): number => {
  const raw = assignment?.max_occurs ?? assignment?.atts_attribute?.max_occurs ?? container.max_occurs ?? 1;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 1;
};

export const buildRepeatableContainerIndex = (
  structure: Partial<AttributeStructure>,
  assignmentById: Record<string, AttsAssignment>
): RepeatableContainerIndex => {
  const index: RepeatableContainerIndex = { byContainerId: {}, byPath: {}, byLeafId: {} };

  const visit = (container: ContainerLike, depth: number) => {
    const assignment = assignmentById[container.assignment_id];
    const maxOccurs = getContainerMaxOccurs(container, assignment);
    if (depth > 0 && maxOccurs > 1) {
      const info: RepeatableContainerInfo = {
        assignmentId: container.assignment_id,
        path: getPostingContainerPath(container),
        displayName: container.frontend_name || assignment?.atts_attribute?.frontend_name || getPostingContainerPath(container),
        maxOccurs,
        leafAssignmentIds: (container.attributeComponents ?? []).map((attribute) => attribute.assignment_id),
      };
      index.byContainerId[String(info.assignmentId)] = info;
      index.byPath[info.path] = info;
      info.leafAssignmentIds.forEach((leafId) => {
        index.byLeafId[String(leafId)] = info;
      });
    }
    (container.containerComponents ?? []).forEach((child) => visit(child as ContainerLike, depth + 1));
  };

  (structure.containerComponents ?? []).forEach((container) => visit(container as ContainerLike, 0));
  return index;
};

/**
 * Rows a repeatable container shows: enough for the highest mapped value_index,
 * at least `minimum` (rows the user added but has not filled yet), and never 0.
 */
export const getContainerRowCount = (mappings: MappingsByAssignment, info: RepeatableContainerInfo, minimum = 0): number => {
  let count = Math.max(1, minimum);
  info.leafAssignmentIds.forEach((leafId) => {
    (mappings[leafId] ?? []).forEach((mapping) => {
      count = Math.max(count, getValueIndex(mapping) + 1);
    });
  });
  return count;
};
